// utils/deepLinkConfig.js
// ===== DEEP LINKING CONFIGURATION =====
// Single source of truth for all share/deep link URLs

/**
 * Canonical host for all share links.
 * NEVER use window.location.origin or vercel.app domains.
 * Using www subdomain since apex redirects to www.
 */
export const CANONICAL_HOST = 'https://www.gweffit.com';

/**
 * Custom URL scheme for fallback deep linking
 */
export const CUSTOM_SCHEME = 'gweffit';

/**
 * Link type definitions with path patterns
 * Adding a new link type = adding one entry here
 */
export const LINK_TYPES = {
  trial: {
    // Short, stable path for trials
    webPath: '/t',
    schemePath: '/t',
    paramName: 'id',
    // Legacy path that also works
    legacyPaths: ['/trial'],
    requiresAuth: false,
  },
  summons: {
    webPath: '/summons',
    schemePath: '/summons',
    paramName: 'token',
    legacyPaths: [],
    requiresAuth: false,
  },
  crew: {
    webPath: '/c',
    schemePath: '/c',
    paramName: 'id',
    legacyPaths: ['/crew/join'],
    requiresAuth: false,
  },
  profile: {
    webPath: '/u',
    schemePath: '/u',
    paramName: 'username',
    legacyPaths: ['/user', '/profile'],
    requiresAuth: false,
  },
  invite: {
    webPath: '/invite',
    schemePath: '/invite',
    paramName: 'code',
    legacyPaths: [],
    requiresAuth: false,
  },
};

/**
 * Generate a canonical share URL
 * @param {string} type - Link type (trial, summons, crew, profile, invite)
 * @param {string} id - The ID/token/code for the link
 * @param {object} options - Optional query params
 * @returns {string} Canonical URL
 *
 * @example
 * getShareUrl('trial', 'abc123') → 'https://www.gweffit.com/t/abc123'
 * getShareUrl('summons', 'xyz789') → 'https://www.gweffit.com/summons/xyz789'
 */
export function getShareUrl(type, id, options = {}) {
  const linkType = LINK_TYPES[type];
  if (!linkType) {
    console.error(`[DeepLink] Unknown link type: ${type}`);
    return `${CANONICAL_HOST}`;
  }

  if (!id) {
    console.error(`[DeepLink] Missing ID for link type: ${type}`);
    return `${CANONICAL_HOST}`;
  }

  let url = `${CANONICAL_HOST}${linkType.webPath}/${encodeURIComponent(id)}`;

  // Add query params if provided
  if (options && Object.keys(options).length > 0) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Dev-only validation
  if (process.env.NODE_ENV === 'development') {
    if (!url.startsWith(CANONICAL_HOST)) {
      console.warn(`[DeepLink] WARNING: Generated URL doesn't start with canonical host: ${url}`);
    }
  }

  return url;
}

/**
 * Generate a custom scheme URL (for fallback deep linking)
 * @param {string} type - Link type
 * @param {string} id - The ID/token/code
 * @returns {string} Custom scheme URL
 *
 * @example
 * getSchemeUrl('trial', 'abc123') → 'gweffit://t/abc123'
 */
export function getSchemeUrl(type, id, options = {}) {
  const linkType = LINK_TYPES[type];
  if (!linkType) {
    console.error(`[DeepLink] Unknown link type: ${type}`);
    return null;
  }

  if (!id) {
    console.error(`[DeepLink] Missing ID for link type: ${type}`);
    return null;
  }

  let url = `${CUSTOM_SCHEME}://${linkType.schemePath}/${encodeURIComponent(id)}`;

  if (options && Object.keys(options).length > 0) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return url;
}

/**
 * Check if a URL is a valid canonical share URL
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
export function isCanonicalUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith(CANONICAL_HOST);
}

/**
 * Validate that a share URL follows the canonical format
 * Use in dev/tests to catch non-canonical URLs
 */
export function validateShareUrl(url, context = '') {
  if (process.env.NODE_ENV === 'development') {
    if (!isCanonicalUrl(url)) {
      console.warn(
        `[DeepLink] Non-canonical share URL detected${context ? ` in ${context}` : ''}: ${url}\n` +
        `Expected URLs to start with: ${CANONICAL_HOST}`
      );
      return false;
    }
  }
  return true;
}

const deepLinkConfig = {
  CANONICAL_HOST,
  CUSTOM_SCHEME,
  LINK_TYPES,
  getShareUrl,
  getSchemeUrl,
  isCanonicalUrl,
  validateShareUrl,
};

export default deepLinkConfig;
